import { useState, useEffect } from "react";
import {
  Check, ChevronDown, Star, ArrowRight, Menu, X,
  CalendarCheck, Users, BarChart3, MessageSquare,
  Smartphone, Globe, Shield, Zap, Clock, TrendingUp,
  Instagram, Twitter, Facebook, Youtube, Linkedin,
  Mail, Phone
} from "lucide-react";
import { LogoIcon } from "../header/Header";
import assets from "../assets/assets";
import Footer from "../Footer/Footer";
import { Helmet } from "react-helmet";

const Logo = () => (
    <div className="flex items-center gap-1 transition-all duration-300 cursor-pointer">
        <LogoIcon />
        <div className="leading-none">
            <a href="/" className="Logo-sans font-semibold text-2xl text-[#ee5219] block">Gemio</a>
            <p className="text-xs pl-0.5 -mt-2.5 text-stone-700">reservations</p>
        </div>
    </div>
);

/* ── NAV ── */
const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

const links = [
    { id: 1, name: "Features" },
    { id: 2, name: "How it works" },
    { id: 3, name: "Pricing" },
    { id: 4, name: "Resources" },
];
  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "bg-white/50  backdrop-blur-sm shadow-md" : "bg-white/30 backdrop-blur-sm"}`}>
      <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
        <Logo />
        <div className="hidden md:flex items-center gap-6">
          {links.map(l => (
            <a key={l.name} href={`#${l.id}`} className="text-sm font-medium text-gray-600 hover:text-[#ee5219] transition-colors flex items-center gap-1">
              {l.name} <ChevronDown size={12} className="text-gray-400" />
            </a>
          ))}
        </div>
        <div className="hidden md:flex items-center gap-3">
          <a href="/dashboard" className="text-sm font-medium text-gray-700 hover:text-[#ee5219] px-3 py-2">Log in</a>
          <a href="https://gemio.app/support" className="text-sm font-medium border border-[#ee5219] text-[#ee5219] px-4 py-2 rounded-lg hover:bg-orange-50 transition-colors">Request Demo</a>
          <a href="/dashboard" className="text-sm font-bold bg-[#ee5219] text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition-colors">Try for free</a>
        </div>
        <button onClick={() => setOpen(!open)} className="md:hidden text-gray-600">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
      {open && (
        <div className="md:hidden bg-white border-t border-gray-100 px-5 py-4 space-y-3">
          {links.map(l => <a key={l.name} href="#" className="block text-sm font-medium text-gray-700 py-1">{l.name}</a>)}
          <div className="pt-3 flex flex-col gap-2 border-t border-gray-100">
            <a href="https://gemio.app/support" className="text-sm font-medium text-center border border-[#ee5219] text-[#ee5219] px-4 py-2.5 rounded-lg">Request Demo</a>
            <a href="/dashboard" className="text-sm font-bold text-center bg-[#ee5219] text-white px-4 py-2.5 rounded-lg">Try for free</a>
          </div>
        </div>
      )}
    </nav>
  );
};

/* ── HERO ── */
const Hero = () => (
  <section className="pt-28 pb-0 bg-gradient-to-br from-orange-50 via-amber-50 to-white overflow-hidden">
    <div className="max-w-6xl mx-auto px-5 text-center">
      {/* Badge */}
      <div className="inline-flex items-center gap-2 bg-orange-100 text-[#ee5219] text-xs font-semibold px-4 py-1.5 rounded-full mb-6">
        <Zap size={13} /> Online reservations system for modern restaurants.
      </div>

      {/* Headline */}
      <h1 className="poppins-semibold text-5xl md:text-6xl lg:text-6xl font-extrabold text-gray-900 mb-6 max-w-4xl mx-auto">
        Streamline your restaurant reservations today.
      </h1>
      <p className="text-lg md:text-xl text-gray-500 max-w-2xl mx-auto mb-8 leading-relaxed">
        Commission-free bookings, seamless online reservations, and powerful table management built for your restaurant.
      </p>

      {/* CTAs */}
      <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-8">
        <a href="https://gemio.app/dashboard" className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#ee5219] hover:bg-orange-700 text-white font-bold px-8 py-4 rounded-xl text-base transition-all shadow-lg shadow-orange-200 hover:shadow-orange-300">
          Get started free <ArrowRight size={18} />
        </a>
        <a href="/support" className="w-full sm:w-auto inline-flex items-center justify-center gap-2 border-2 border-gray-200 text-gray-700 font-semibold px-8 py-4 rounded-xl text-base hover:border-[#ee5219] hover:text-[#ee5219] transition-all">
          Request a demo
        </a>
      </div>

      {/* Trust badges */}
      <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-gray-500 mb-10">
        {[
          "Commission-free bookings",
          "Flexible plans",
          "Trusted by 100+ restaurants",
        ].map((t, i) => (
          <span key={i} className="flex items-center gap-1.5">
            <Check size={15} className="text-[#ee5219]" /> {t}
          </span>
        ))}
      </div>

      {/* Rating */}
      <div className="flex items-center justify-center gap-2 mb-10">
        <div className="flex">
          {[1,2,3,4].map(i => <Star key={i} size={20} fill="#f59e0b" stroke="none" />)}
          <Star size={20} fill="#fde68a" stroke="none" />
        </div>
        <span className="font-bold text-gray-800">4.7</span>
        <span className="text-gray-400 text-sm">|</span>
        <span className="text-gray-500 text-sm">150+ happy restaurants across Kenya</span>
      </div>

      {/* App screenshot placeholder */}
      <div className="relative mx-auto max-w-5xl">
        {/* Glow */}
        <div className="absolute inset-0 bg-gradient-to-b from-orange-100/60 to-transparent rounded-3xl blur-2xl -z-10 scale-95" />

        {/* Main tablet mockup */}
        <div className="relative bg-gray-900 rounded-2xl shadow-2xl overflow-hidden border-4 border-gray-800 mx-auto" style={{ maxWidth: 820 }}>
          {/* Browser bar */}
          <div className="bg-gray-800 px-4 py-2.5 flex items-center gap-2">
            <div className="flex gap-1.5">
              <div className="w-3 h-3 rounded-full bg-red-400" />
              <div className="w-3 h-3 rounded-full bg-yellow-400" />
              <div className="w-3 h-3 rounded-full bg-green-400" />
            </div>
            <div className="flex-1 bg-gray-700 rounded-md mx-4 px-3 py-1 text-xs text-gray-400 text-center">
              gemio.app/reservations
            </div>
          </div>

          {/* Placeholder content */}
          <div className="bg-gradient-to-br from-gray-100 to-gray-200 aspect-video flex flex-col items-center justify-center gap-4 relative">
            <div className="absolute inset-0 opacity-10" style={{
              backgroundImage: "repeating-linear-gradient(0deg,transparent,transparent 39px,#aaa 39px,#aaa 40px),repeating-linear-gradient(90deg,transparent,transparent 39px,#aaa 39px,#aaa 40px)"
            }} />
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl text-center shadow-lg relative">
              <img className="w-full h-auto rounded" 
                src={assets.reservation_dashboard_placeholder} 
                alt="Reservation Dashboard" />
            </div>
          </div>
        </div>

        {/* Floating mobile mockup */}
        <div className="absolute -right-4 md:right-2 bottom-4 hidden sm:block">
          <div className="bg-gray-900 rounded-[2rem] shadow-2xl border-4 border-gray-800 overflow-hidden" style={{ width: 160, height: 290 }}>
            <div className="bg-gray-800 h-6 flex items-center justify-center">
              <div className="w-12 h-1.5 bg-gray-600 rounded-full" />
            </div>
            <div className="bg-gradient-to-b from-gray-100 to-gray-200 h-full flex flex-col items-center justify-center">
              <img src={assets.phone_placeholder} className="w-full rounded-lg shadow" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ── LOGOS STRIP ── */
const TrustStrip = () => (
  <section className="py-10 border-y border-gray-100 bg-white">
    <div className="max-w-6xl mx-auto px-5">
      <p className="text-center text-xs font-semibold text-gray-400 uppercase tracking-widest mb-6">Trusted by Kenya's top restaurants</p>
      <div className="flex flex-wrap items-center justify-center gap-8 md:gap-12">
        {/* {["Harvest", "Tamu Tamu", "Nyama Mama", "The Alchemist", "Brew Bistro", "Carnivore"].map((r, i) => (
          <span key={i} className="text-gray-300 font-bold text-lg tracking-wide uppercase">{r}</span>
        ))} */}
      </div>
    </div>
  </section>
);

/* ── FEATURES GRID ── */
const Features = () => {
  const features = [
    { icon: CalendarCheck, title: "Online Reservation System", desc: "Let guests book tables 24/7 directly from your Gemio listing. Zero commissions, instant confirmations." },
    { icon: Users, title: "Table & Guest Management", desc: "View seating, manage party sizes, track upcoming and seated guests from a single dashboard." },
    { icon: MessageSquare, title: "Automated Guest Messaging", desc: "Send instant booking confirmations and reminders via email so guests never miss their reservation." },
    { icon: BarChart3, title: "Reports & Insights", desc: "Track covers, peak booking times, and reservation trends to optimise your operations." },
    { icon: Smartphone, title: "Mobile-First Dashboard", desc: "Manage everything from your phone. Full-featured dashboard built for on-the-go restaurant teams." },
    { icon: Globe, title: "QR Digital Menus", desc: "Guests scan your QR code to browse your full menu before or during their visit — no printing needed." },
  ];
  return (
    <section className="py-20 bg-white" id="features">
      <div className="max-w-6xl mx-auto px-5">
        <div className="text-center mb-14">
          <p className="text-xs font-semibold text-[#ee5219] uppercase tracking-widest mb-3">Everything you need</p>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Powerful tools for modern restaurants</h2>
          <p className="text-gray-500 text-lg max-w-xl mx-auto">Everything you need to manage reservations, delight guests, and grow your restaurant in one platform.</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map((f, i) => (
            <div key={i} className="group border border-gray-100 rounded-2xl p-6 hover:border-orange-200 hover:shadow-lg hover:shadow-orange-50 transition-all">
              <div className="w-11 h-11 bg-orange-50 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#ee5219] transition-colors">
                <f.icon size={20} className="text-[#ee5219] group-hover:text-white transition-colors" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">{f.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ── HOW IT WORKS ── */
const HowItWorks = () => (
  <section className="py-20 bg-gradient-to-br from-orange-50 to-amber-50" id="how-it-works">
    <div className="max-w-6xl mx-auto px-5">
      <div className="text-center mb-14">
        <p className="text-xs font-semibold text-[#ee5219] uppercase tracking-widest mb-3">Simple setup</p>
        <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Streamline your restaurant<br />operations today</h2>
        <p className="text-gray-500 text-lg max-w-xl mx-auto">Get up and running in minutes. No technical knowledge required.</p>
      </div>

      {/* Steps row */}
      <div className="grid md:grid-cols-3 gap-6 mb-14">
        {[
          { n: "1", title: "Create your listing", desc: "Set up your restaurant profile with photos, menu, location, and opening hours." },
          { n: "2", title: "Enable reservations", desc: "Toggle online booking on from your dashboard and set your available time slots." },
          { n: "3", title: "Manage & grow", desc: "Accept bookings, view analytics, and delight guests - all from one dashboard." },
        ].map((s, i) => (
          <div key={i} className="relative">
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-orange-100 h-full">
              <div className="w-10 h-10 bg-[#ee5219] text-white font-extrabold text-lg rounded-xl flex items-center justify-center mb-4">{s.n}</div>
              <h3 className="font-bold text-gray-900 text-lg mb-2">{s.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
            </div>
            {i < 2 && <div className="hidden md:block absolute top-10 -right-3 z-10 text-gray-300 text-2xl font-thin">→</div>}
          </div>
        ))}
      </div>

      {/* Big app screenshot */}
      <div className="grid lg:grid-cols-2 gap-8 items-center">
        <div>
          <h3 className="text-3xl font-bold text-gray-900 mb-1">Your reservations dashboard, at a glance</h3>
          <p className="text-gray-500 mb-6 leading-relaxed">See upcoming reservations, seated guests, and live availability — all in real time. Manage dining sections, party sizes, and booking status without the chaos.</p>
          <ul className="space-y-3">
            {[
              "View upcoming and seated reservations in one screen",
              "Manage table sections: Main dining, Terrace, VIP",
              "Toggle online booking on/off instantly",
              "Works on desktop and mobile",
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm text-gray-700">
                <span className="mt-0.5 w-5 h-5 rounded-full bg-[#ee5219] flex items-center justify-center flex-shrink-0">
                  <Check size={11} className="text-white" />
                </span>
                {item}
              </li>
            ))}
          </ul>
          <div className="mt-8">
            <a href="/dashboard" className="inline-flex items-center gap-2 bg-[#ee5219] text-white font-bold px-6 py-3.5 rounded-xl hover:bg-orange-700 transition-colors">
              Get started free <ArrowRight size={16} />
            </a>
          </div>
        </div>

        {/* Screenshot placeholder */}
        <div className="relative">
          <div className="bg-gray-900 rounded-2xl overflow-hidden shadow-2xl border-4 border-gray-800">
            <div className="bg-gray-800 px-4 py-2.5 flex items-center gap-2">
              <div className="flex gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
                <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
                <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
              </div>
              <div className="flex-1 bg-gray-700 rounded px-3 py-0.5 text-xs text-gray-400">gemio.app/reservations</div>
            </div>
            <div className="bg-gradient-to-br from-gray-100 to-gray-200 aspect-[4/3] flex items-center justify-center relative">
              <div className="absolute inset-0 opacity-5" style={{ backgroundImage: "repeating-linear-gradient(0deg,transparent,transparent 29px,#999 29px,#999 30px),repeating-linear-gradient(90deg,transparent,transparent 29px,#999 29px,#999 30px)" }} />
                <img src={assets.dash_screen}/>
              
            </div>
          </div>

          {/* Floating stat card */}
          <div className="absolute -bottom-4 -left-4 bg-white rounded-xl shadow-xl border border-gray-100 px-4 py-3 flex items-center gap-3">
            <div className="w-10 h-10 bg-green-50 rounded-xl flex items-center justify-center">
              <TrendingUp size={18} className="text-green-500" />
            </div>
            <div>
              <p className="text-xs text-gray-400">This week</p>
              <p className="text-sm font-bold text-gray-900">+34 new covers</p>
            </div>
          </div>
          {/* Floating mini card */}
          <div className="absolute -top-4 -right-4 bg-white rounded-xl shadow-xl border border-gray-100 px-4 py-3">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-green-400" />
              <p className="text-xs font-semibold text-gray-700">Reservations Online</p>
            </div>
            <p className="text-xs text-gray-400 mt-0.5">12 upcoming today</p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ── MOBILE SECTION ── */
const MobileSection = () => (
  <section className="py-20 bg-white">
    <div className="max-w-6xl mx-auto px-5">
      <div className="grid lg:grid-cols-2 gap-12 items-center">
        {/* Phones placeholder */}
        <div className="flex justify-center gap-4 relative">
          {/* Phone 1 */}
          <div className="bg-gray-900 rounded-[2.5rem] border-4 border-gray-800 overflow-hidden shadow-2xl" style={{ width: 180, height: 360 }}>
            <div className="bg-gray-800 h-7 flex items-center justify-center">
              <div className="w-14 h-2 bg-gray-600 rounded-full" />
            </div>
            <div className="bg-gradient-to-b from-orange-50 to-gray-100 h-full flex items-center justify-center">
              <img src={assets.bookings}/>
            </div>
          </div>
          {/* Phone 2 (offset) */}
          <div className="bg-gray-900 rounded-[2.5rem] border-4 border-gray-800 overflow-hidden shadow-2xl mt-10" style={{ width: 180, height: 360 }}>
            <div className="bg-gray-800 h-7 flex items-center justify-center">
              <div className="w-14 h-2 bg-gray-600 rounded-full" />
            </div>
            <div className="bg-gradient-to-b from-amber-50 to-gray-100 h-full flex items-center justify-center">
              <img src={assets.phone_placeholder}/>
            </div>
          </div>
        </div>

        <div>
          <p className="text-xs font-semibold text-[#ee5219] uppercase tracking-widest mb-3">Mobile-first</p>
          <h2 className="text-4xl font-extrabold text-gray-900 mb-4">Manage from anywhere, anytime</h2>
          <p className="text-gray-500 leading-relaxed mb-6">
            Your full reservations dashboard in your pocket. Confirm bookings, seat guests, and view your floor plan from your phone, as the owner.
          </p>
          <ul className="space-y-3 mb-8">
            {[
              "Real-time reservation updates",
              "One-tap guest confirmation",
              "Live table availability view",
              "Digitized menu management",
            ].map((item, i) => (
              <li key={i} className="flex items-center gap-2.5 text-sm text-gray-700">
                <Check size={16} className="text-[#ee5219] flex-shrink-0" /> {item}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  </section>
);

/* ── PRICING ── */
const Pricing = () => {
  const plans = [
    {
      name: "Starter",
      price: "Free",
      period: "",
      desc: "Perfect for restaurants just getting started with online reservations.",
      features: ["Up to 50 covers/month", "Basic reservation management", "Digital menu (QR code)", "Email confirmations"],
      cta: "Get started free",
      highlight: false,
    },
    {
      name: "Essential",
      price: "KES 2,500",
      period: "/mo",
      desc: "Reservation & table management with unlimited monthly bookings.",
      features: ["Unlimited covers/month", "Table management dashboard", "Guest messaging & reminders", "Reservation analytics", "Priority support"],
      cta: "Start free trial",
      highlight: true,
    },
    {
      name: "Pro",
      price: "KES 5,500",
      period: "/mo",
      desc: "Full control and flexibility to enhance the guest experience.",
      features: ["Everything in Essential", "Advanced analytics & reports", "Multiple dining sections", "Custom branding", "Dedicated success manager", "All future add-ons"],
      cta: "Contact sales",
      highlight: false,
    },
  ];

  return (
    <section className="py-20 bg-gray-50" id="pricing">
      <div className="max-w-6xl mx-auto px-5">
        <div className="text-center mb-14">
          <p className="text-xs font-semibold text-[#ee5219] uppercase tracking-widest mb-3">Pricing</p>
          <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-4">Get more customers, for less</h2>
          <p className="text-gray-500 text-lg">Commission-free reservations on every plan. No hidden fees.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {plans.map((p, i) => (
            <div key={i} className={`rounded-2xl p-7 flex flex-col ${p.highlight ? "bg-[#ee5219] text-white shadow-2xl shadow-orange-200 scale-105" : "bg-white border border-gray-200 shadow-sm"}`}>
              <div className="mb-4">
                <h3 className={`text-xl font-extrabold mb-1 ${p.highlight ? "text-white" : "text-gray-900"}`}>{p.name}</h3>
                <p className={`text-sm leading-snug ${p.highlight ? "text-orange-100" : "text-gray-500"}`}>{p.desc}</p>
              </div>
              <div className="mb-6">
                <span className={`text-4xl font-extrabold ${p.highlight ? "text-white" : "text-gray-900"}`}>{p.price}</span>
                <span className={`text-sm ${p.highlight ? "text-orange-200" : "text-gray-400"}`}>{p.period}</span>
              </div>
              <ul className="space-y-2.5 mb-8 flex-1">
                {p.features.map((f, j) => (
                  <li key={j} className={`flex items-start gap-2 text-sm ${p.highlight ? "text-orange-50" : "text-gray-600"}`}>
                    <Check size={15} className={`flex-shrink-0 mt-0.5 ${p.highlight ? "text-orange-200" : "text-[#ee5219]"}`} /> {f}
                  </li>
                ))}
              </ul>
              <a href="#" className={`text-center font-bold py-3 rounded-xl transition-all text-sm ${p.highlight ? "bg-white text-[#ee5219] hover:bg-orange-50" : "bg-[#ee5219] text-white hover:bg-orange-700"}`}>
                {p.cta}
              </a>
            </div>
          ))}
        </div>
        <p className="text-center text-xs text-gray-400 mt-8">All prices in Kenyan Shillings. Cancel anytime. No contracts.</p>
      </div>
    </section>
  );
};

/* ── FINAL CTA ── */
const FinalCTA = () => (
  <section className="py-24 bg-gradient-to-br from-gray-900 to-gray-800 relative overflow-hidden">
    <div className="absolute inset-0 opacity-10"
      style={{ backgroundImage: "radial-gradient(circle at 30% 50%, #ee5219 0%, transparent 60%)" }} />
    <div className="max-w-3xl mx-auto px-5 text-center relative z-10">
      <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-4 leading-tight">
        Ready to fill more tables?
      </h2>
      <p className="text-gray-400 text-lg mb-8">
        Join 100+ Kenyan restaurants already using Gemio to manage reservations and delight their guests.
      </p>
      <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
        <a href="/dashboard" className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#ee5219] hover:bg-orange-600 text-white font-bold px-8 py-4 rounded-xl text-base transition-all shadow-lg">
          Get started free <ArrowRight size={18} />
        </a>
        <a href="/support" className="w-full sm:w-auto inline-flex items-center justify-center border-2 border-gray-600 text-gray-300 font-semibold px-8 py-4 rounded-xl text-base hover:border-orange-400 hover:text-orange-300 transition-all">
          Request a demo
        </a>
      </div>
      <p className="mt-5 text-xs text-gray-500">No credit card required · Free plan available · Cancel anytime</p>
    </div>
  </section>
);

/* ── PAGE ── */
export default function GemioReservationsLanding() {
  return (
    <>
    <Helmet>
    <title>Restaurant Reservation & Table Management Platform | Gemio</title>

    <meta
      name="description"
      content="Gemio helps restaurants manage reservations, tables and floor plans in one platform. Streamline bookings, organize seating and improve restaurant operations."
    />
    <link rel="canonical" href="https://gemio.app/reservation" />

    {/* Open Graph */}

    <meta property="og:type" content="website" />
    <meta property="og:title" content="Restaurant Reservation Management Platform | Gemio" />
    <meta
      property="og:description"
      content="Manage restaurant reservations, tables and floor plans with Gemio. A modern reservation management platform for restaurants."
    />
    <meta property="og:url" content="https://gemio.app/reservation" />
    <meta property="og:site_name" content="Gemio" />
    <meta property="og:image" content="https://gemio.app/gemio_logo.jpg" />

    {/* Twitter */}

    <meta name="twitter:card" content="summary_large_image" />
    <meta name="twitter:title" content="Restaurant Reservation Management Platform | Gemio" />
    <meta
      name="twitter:description"
      content="A reservation and table management platform built for restaurants."
    />
    <meta name="twitter:image" content="https://gemio.app/gemio_logo.jpg" />
    <link rel="canonical" href="https://gemio.app/reservation" />

    {/* Structured Data */}
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "Gemio Restaurant Reservation Management",
          "applicationCategory": "BusinessApplication",
          "operatingSystem": "Web",
          "url": "https://gemio.app/reservation",
          "description": "Gemio is a restaurant reservation and table management platform that helps restaurants manage bookings, organize floor layouts and streamline dining operations.",
          "provider": {
            "@type": "Organization",
            "name": "Gemio",
            "url": "https://gemio.app/reservation"
          },
          "featureList": [
            "Reservation management",
            "Table management",
            "Restaurant floor planning",
            "Guest tracking",
            "Booking management"
          ],
          "areaServed": {
            "@type": "Country",
            "name": "Kenya"
          }
        })
      }}
    />

    </Helmet>
    <div className="font-sans antialiased">
      <Navbar />
      <Hero />
      <TrustStrip />
      <Features />
      <HowItWorks />
      <MobileSection />
      {/* <Pricing /> */}
      <FinalCTA />
      <Footer description={"Empowering Kenya's restaurants, one table at a time."}/>
    </div>
    </>
  );
}
